import dayjs from "dayjs";
import {useEffect, useMemo, useRef, useState} from "react";
import _ from "lodash";
import {defaultDataGridWithSelectProperties, formatCompanyName} from "../utils/DataGridUtils";
import {MaterialReactTable, useMaterialReactTable} from "material-react-table";
import AwardsDataGridDetailPanel from "./AwardsDataGridDetailPanel";
import {Stack} from "@mui/material";
import DataGridHeader from "../DataGridHeader";

const convertToDate = (value) => {
    return dayjs(value, "YYYY-MM-DD").toDate();
};

const formatDate = (value) => {
    const date = dayjs(value);
    return date.format("DD/MM/YY");
};

const availableItems = (data) => {
    return data?.rows ? data?.rows.map((item) => item.id) : [];
};

const AwardsDataGrid = ({data, isLoading, filters, updateFilters}) => {
    const [rowSelectionModel, setRowSelectionModel] = useState({});
    const [rowCount, setRowCount] = useState(0);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (!isLoading) {

            const available = availableItems(data);
            const shouldBeSelected = _.difference(available, filters.ignoredAwards);

            const previousRowCount = rowCount;
            const newRowCount = available.length;

            if (shouldBeSelected.length > 0 && (isFirstRender.current || previousRowCount !== newRowCount)) {
                const initialRowSelectionModel = shouldBeSelected.reduce((row, id) => {
                    row[id] = true;
                    return row;
                }, {});

                setRowCount(newRowCount);
                setRowSelectionModel(initialRowSelectionModel);
                isFirstRender.current = false;
            }
        }
    }, [data, isLoading, filters, rowCount]);

    const rows = data?.rows ? data.rows : [];

    const columns = useMemo(() => [
            {
                accessorKey: "effectiveDate",
                header: "Effective Date",
                accessorFn: row => convertToDate(row.effectiveDate),
                Cell: ({renderedCellValue}) => (<>{formatDate(renderedCellValue)}</>),
                muiTableBodyCellProps: {
                    align: 'center',
                },
                maxSize: 143,
                grow: false,
            },
            {
                accessorKey: "companyName",
                header: "Organisation",
                Cell: ({renderedCellValue}) => (<>{formatCompanyName(renderedCellValue)}</>),
                size: 200,
                sortingFn: 'companyNameSort',
            },
            {
                accessorKey: "increase",
                header: "% Increase",
                muiTableBodyCellProps: {
                    align: 'right',
                },
                size: 123,
                grow: false,
            },
            {
                accessorKey: "increaseType",
                header: "Increase Type",
                size: 143,
                grow: false,
            },
            {
                accessorKey: "employeeGroup",
                header: "Employee Group",
                size: 200,
            },
            {
                accessorKey: "numberOfEmployees",
                header: "No. employees",
                Cell: ({renderedCellValue}) => (<>{renderedCellValue.toLocaleString()}</>),
                muiTableBodyCellProps: {
                    align: 'right',
                },
                size: 149,
                grow: false,
            },
            {
                accessorKey: "longTermDeal",
                header: "Long Term Deal",
                Cell: ({renderedCellValue}) => (<>{renderedCellValue ? "Yes" : "No"}</>),
                muiTableBodyCellProps: {
                    align: 'center',
                },
                size: 155,
                grow: false,
            },
            {
                accessorKey: "unionRecognised",
                header: "Union recognised",
                Cell: ({renderedCellValue}) => (<>{renderedCellValue ? "Yes" : "No"}</>),
                muiTableBodyCellProps: {
                    align: 'center',
                },
                size: 168,
                grow: false,
            },
            {
                accessorKey: "unionDetails",
                header: "Unions",
                size: 353,
            },
            {
                accessorKey: "sectorName",
                header: "Sector",
                size: 193,
            },
            {
                accessorKey: "industryName",
                header: "Industry",
                size: 233,
            },
            {
                accessorKey: "subSectorName",
                header: "Sub Sector",
                size: 320,
            },
        ],
        []);

    const table = useMaterialReactTable({
        ...defaultDataGridWithSelectProperties,
        columns: columns,
        data: rows,
        initialState: {
            ...defaultDataGridWithSelectProperties.initialState,
            sorting: [{id: "companyName", desc: false}],
            columnVisibility: {
                increaseType: false,
                longTermDeal: false,
                unionRecognised: false,
                unionDetails: false,
                sectorName: false,
                industryName: false,
                subSectorName: false,
            },
        },
        state: {
            rowSelection: rowSelectionModel,
        },
        onRowSelectionChange: setRowSelectionModel,
        sortingFns: {
            companyNameSort: (row1, row2) => {
                const companyName1 = row1.original.companyName;
                const companyName2 = row2.original.companyName;
                if (companyName1 === null || companyName2 === null) {
                    if (companyName1 === companyName2) {
                        return 0;
                    }

                    return companyName1 == null ? 1 : -1;
                }

                return companyName1.localeCompare(companyName2);
            }
        },
        renderDetailPanel: ({row}) => <AwardsDataGridDetailPanel row={row.original}/>,
    });

    useEffect(() => {
        const available = availableItems(data);
        if (available.length > 0) {
            const currentlyIgnored = filters.ignoredAwards || [];
            const selected = table.getSelectedRowModel().rows.map((row) => row.id);
            const ignored = _.difference(available, selected);

            if (currentlyIgnored.length !== ignored.length) {
                filters.ignoredAwards = ignored;
                updateFilters();
            }
        }
    }, [table, rowSelectionModel, data, filters, updateFilters]);

    return (
        <Stack id="data_grid" name="data_grid">
            <DataGridHeader table={table}/>
            <MaterialReactTable table={table}/>
        </Stack>
    );
};

export default AwardsDataGrid;
